import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useResetPasswordMutation } from '../../services/userAuthApi';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'; // Import eye icons
import { useTranslation } from 'react-i18next';
import usePopupMsg from '../../hooks/usePopupMsg';

const ResetPassword = () => {
  const [serverError, setServerError] = useState({});
  const [serverMsg, setServerMsg] = useState({});
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const { id, token } = useParams();
  const navigate = useNavigate();
  const {t} = useTranslation("auth");
  const translation = t('reset_password_tran');

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const actualData = {
      password: data.get('password'),
      password2: data.get('password2'),
    };

    const res = await resetPassword({ actualData, id, token });

    if (res.error) {
      setServerMsg({}); // Clear success message on error
      // Ensure errors object is properly handled,
      // particularly non_field_errors which might be an array
      setServerError({
        ...res.error.data?.errors,
        non_field_errors: res.error.data?.errors?.non_field_errors || [],
      });
    } else if (res.data) { // Use else if to ensure only one state is set
      setServerError({}); // Clear errors on success
      setServerMsg(res.data);
      document.getElementById('password-reset-form').reset();
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000); // Redirect after 3 seconds
      usePopupMsg.getState().open("Your password has been updated successfully.", 'success')
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-gray-700">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white text-center mb-6">
          {translation.heading}
        </h1>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-6 text-base">
          {translation.description}
        </p>

        <form onSubmit={handleSubmit} noValidate id="password-reset-form" className="space-y-6">
          <div className="relative"> {/* Added relative for positioning eye icon */}
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {translation.new_password_label}
            </label>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50 dark:bg-gray-700"
              placeholder={translation.new_password_placeholder}
            />
            <span
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 dark:text-gray-500 top-7"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <AiFillEyeInvisible className="h-5 w-5" /> : <AiFillEye className="h-5 w-5" />}
            </span>
            {serverError.password && (
              <p className="az-error-message">{serverError.password[0]}</p>
            )}
          </div>

          <div className="relative"> {/* Added relative for positioning eye icon */}
            <label htmlFor="password2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {translation.confirm_password_label}
            </label>
            <input
              id="password2"
              name="password2"
              type={showPassword2 ? 'text' : 'password'}
              required
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50 dark:bg-gray-700"
              placeholder={translation.confirm_password_placeholder}
            />
            <span
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 dark:text-gray-500 top-7"
              onClick={() => setShowPassword2((prev) => !prev)}
            >
              {showPassword2 ? <AiFillEyeInvisible className="h-5 w-5" /> : <AiFillEye className="h-5 w-5" />}
            </span>
            {serverError.password2 && (
              <p className="az-error-message">{serverError.password2[0]}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 transition duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                translation.update_button
              )}
            </button>
          </div>

          {serverError.non_field_errors && (
            <div className="mt-4 text-center text-sm text-red-600 dark:text-red-400">
              {serverError.non_field_errors.map((error, index) => (
                <p key={index} className='az-error-message'>{error}</p>
              ))}
            </div>
          )}

          {serverMsg.msg && (
            <p className="mt-4 text-center text-sm text-green-600 dark:text-green-400 font-medium">
              {serverMsg.msg}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;